import { useCallback, useEffect, useState } from 'react'
import { leadAdsApi, type LeadAdsForm, type LeadAdsFormsList } from '../api/integrations'
import { campaignsApi, type Campaign } from '../api/campaigns'
import {
  FormGrid, Button, IconButton, Callout, EmptyState, Card, CardHeader, CardContent, DataTable,
  StatusPill, TextField, Select, useConfirm,
} from '../components/ui'

/**
 * Z-10/Z-11: scheda "Lead Ads". Google Ads: un modulo per campagna, con l'URL e la chiave da incollare nel
 * modulo lead di Google Ads (webhook). Meta: un solo webhook per l'app, con la verifica della firma
 * `X-Hub-Signature-256`; i segreti (app secret, token di verifica, token di pagina) stanno nella
 * configurazione del server, qui si vede solo se sono presenti.
 */
const PLATFORM_LABEL: Record<string, string> = { google_ads: 'Google Ads', meta_ads: 'Meta' }

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('it-IT') : '—'
}

function errText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="d-flex gap-2 align-items-start flex-wrap">
      <div className="flex-grow-1">
        <TextField label={label} className="font-monospace" readOnly value={value} onFocus={e => e.target.select()} />
      </div>
      <Button variant="outlined"
        onClick={() => { void navigator.clipboard?.writeText(value).then(() => setCopied(true)) }}>
        {copied ? 'Copiato' : 'Copia'}
      </Button>
    </div>
  )
}

export function LeadAdsTab() {
  const askConfirm = useConfirm()
  const [data, setData] = useState<LeadAdsFormsList | null>(null)
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [error, setError] = useState<string | null>(null)
  const [shown, setShown] = useState<LeadAdsForm | null>(null)
  const [form, setForm] = useState({ platform: 'google_ads', name: '', externalRef: '', campaignId: '' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const [d, camp] = await Promise.all([leadAdsApi.list(), campaignsApi.list()])
      setData(d)
      setCampaigns(camp.items)
      setError(null)
    } catch (e) {
      setError(errText(e, 'Errore nel caricamento'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function create() {
    if (!form.name.trim()) return setError('Il nome è obbligatorio')
    setSaving(true)
    setError(null)
    try {
      const created = await leadAdsApi.create({
        platform: form.platform,
        name: form.name.trim(),
        externalRef: form.externalRef.trim() || null,
        campaignId: form.campaignId ? Number(form.campaignId) : null,
      })
      setShown(created)
      setForm(f => ({ ...f, name: '', externalRef: '', campaignId: '' }))
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nella creazione'))
    } finally {
      setSaving(false)
    }
  }

  async function show(f: LeadAdsForm) {
    try {
      setShown(await leadAdsApi.get(f.id))
    } catch (e) {
      setError(errText(e, 'Errore nella lettura'))
    }
  }

  async function rotate(f: LeadAdsForm) {
    if (!(await askConfirm({ title: 'Ruota chiave', message: `Ruotare la chiave di "${f.name}"? La vecchia smette di funzionare subito: va aggiornata in Google Ads.`, confirmLabel: 'Ruota', destructive: true }))) return
    try {
      setShown(await leadAdsApi.rotateKey(f.id))
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nella rotazione'))
    }
  }

  async function toggle(f: LeadAdsForm) {
    try {
      await leadAdsApi.update(f.id, { name: f.name, campaignId: f.campaignId, isActive: !f.isActive })
      await load()
    } catch (e) {
      setError(errText(e, 'Errore nel salvataggio'))
    }
  }

  const meta = data?.meta
  const yesNo = (ok: boolean | undefined) =>
    ok ? <StatusPill tone="success">Presente</StatusPill> : <StatusPill tone="warning">Mancante</StatusPill>

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      {shown && (
        <Callout tone="warning">
          <div className="mb-2 md-typescale-emphasized-body-medium">
            {PLATFORM_LABEL[shown.platform] ?? shown.platform}: {shown.name}
          </div>
          <div className="d-flex flex-column gap-2">
            <CopyField label="URL del webhook" value={shown.webhookUrl} />
            {shown.key && <CopyField label="Chiave (google_key)" value={shown.key} />}
            <div><Button variant="text" onClick={() => setShown(null)}>Chiudi</Button></div>
          </div>
        </Callout>
      )}

      <Card variant="outlined" padding="none">
        <CardHeader title="Meta Lead Ads" />
        <CardContent>
          <div className="d-flex flex-column gap-2">
            <p className="text-muted md-typescale-body-small mb-0">
              Un solo webhook per l&apos;app Meta. Il CRM verifica la firma <code>X-Hub-Signature-256</code> e legge il
              lead dal Graph API con il token di pagina. I segreti si impostano nella configurazione del server
              (sezione <code>LeadAds:Meta</code>), non da qui. Attivo solo con l&apos;interruttore &quot;Lead da Meta&quot;.
            </p>
            {meta && <CopyField label="URL di richiamo da inserire in Meta" value={meta.webhookUrl} />}
            <div className="d-flex gap-3 flex-wrap md-typescale-body-small">
              <span>Token di verifica {yesNo(meta?.verifyTokenConfigured)}</span>
              <span>App secret {yesNo(meta?.appSecretConfigured)}</span>
              <span>Token di pagina {yesNo(meta?.pageTokenConfigured)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" padding="none">
        <CardHeader title="Nuovo modulo lead" />
        <CardContent>
          <div className="d-flex flex-column gap-3">
            <p className="text-muted md-typescale-body-small mb-0">
              Google Ads: crea un modulo per ogni campagna, poi incolla URL e chiave nella sezione webhook del modulo
              lead. I lead arrivano con fonte Ads e canale Google Ads; lo stesso lead inviato due volte ne crea uno solo.
              Attivo solo con l&apos;interruttore &quot;Lead da Google Ads&quot;.
            </p>
            <FormGrid columns={4}>
              <div>
                <Select id="la-platform" label="Piattaforma" value={form.platform}
                  onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}>
                  <option value="google_ads">Google Ads</option>
                  <option value="meta_ads">Meta</option>
                </Select>
              </div>
              <div>
                <TextField id="la-name" label="Nome" maxLength={100} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <TextField id="la-ref" label={form.platform === 'meta_ads' ? 'Id del modulo Meta' : 'Id campagna Google (facoltativo)'}
                  value={form.externalRef} onChange={e => setForm(f => ({ ...f, externalRef: e.target.value }))} />
              </div>
              <div>
                <Select id="la-campaign" label="Campagna CRM (facoltativa)" value={form.campaignId}
                  onChange={e => setForm(f => ({ ...f, campaignId: e.target.value }))}>
                  <option value="">Nessuna</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div>
                <Button variant="tonal" fullWidth disabled={saving} onClick={() => void create()}>Crea</Button>
              </div>
            </FormGrid>
          </div>
        </CardContent>
      </Card>

      <Card variant="outlined" padding="none">
        {!data || data.items.length === 0 ? (
          <EmptyState icon="campaign" title="Nessun modulo." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr><th>Nome</th><th>Piattaforma</th><th>Campagna CRM</th><th>Ultimo lead</th><th>Stato</th><th>Azioni</th></tr>
            </thead>
            <tbody>
              {data.items.map(f => (
                <tr key={f.id}>
                  <td className="md-typescale-emphasized-body-medium">{f.name}</td>
                  <td>{PLATFORM_LABEL[f.platform] ?? f.platform}</td>
                  <td>{campaigns.find(c => c.id === f.campaignId)?.name ?? '—'}</td>
                  <td>{fmt(f.lastReceivedAt)}</td>
                  <td>
                    {f.isActive ? <StatusPill tone="success">Attivo</StatusPill> : <StatusPill tone="neutral">Sospeso</StatusPill>}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      {f.platform === 'google_ads' && (
                        <>
                          <IconButton icon="visibility" size="sm" aria-label="Mostra URL e chiave" onClick={() => void show(f)} />
                          <IconButton icon="autorenew" size="sm" aria-label="Ruota la chiave" onClick={() => void rotate(f)} />
                        </>
                      )}
                      <IconButton icon={f.isActive ? 'pause' : 'play_arrow'} size="sm"
                        aria-label={f.isActive ? 'Sospendi' : 'Riattiva'} onClick={() => void toggle(f)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>
    </>
  )
}
